"use client";

import { useState, useRef, useEffect, useCallback, useMemo } from "react";

// ─────────────────────────────────────────────────────────────────────────────
// Types
// ─────────────────────────────────────────────────────────────────────────────

interface Tool {
  uuid: string;
  name: string;
  category: string;
  description: string;
  tags: string[];
  image_url: string;
  status: "available" | "lent" | "reserved" | "maintenance";
  condition: "good" | "fair" | "needs_repair";
  requires_training: boolean;
  daily_rate: number;
  subscription_included: boolean;
  controller_id?: string;
  locker_number?: number;
  item_type?: string;  // "tool" | "material" — used for pricing
  is_free?: boolean;
  picked_up?: boolean;
  pickup_preference?: string;   // "pickup" (community) | "apoteket" | "delivery"
  pickup_address?: string;
  pickup_time_span?: string;
  pickup_instructions?: string;
  pickup_description?: string;
  donated_by?: string;
}

interface Booking {
  booking_id: string;
  user_name: string;
  tool_uuids: string[];
  start_date: string;
  end_date: string;
  actual_return_date: string | null;
  billing_type: string;
  status: string;
  odoo_invoice_number: string | null;
  total_amount: number;
}

interface Recommendation {
  uuid: string;
  name: string;
  category: string;
  description: string;
  status: string;
  reason: string;
}

interface ReservationSlot {
  booking_id: string;
  start_date: string;
  end_date: string;
}

interface PlannerResult {
  uuid: string;
  name: string;
  category: string;
  daily_rate: number;
  available: boolean;
  blocking: { start_date: string; end_date: string }[];
  next_available_from: string | null;
  alternatives: { uuid: string; name: string; daily_rate: number; subscription_included?: boolean }[];
}

interface PlannerResponse {
  requested: { start_date: string; end_date: string };
  results: PlannerResult[];
  all_available: boolean;
  common_next_date: string | null;
  shop_suggestions: { tools: string[]; suggestion: string }[];
}

const CATEGORY_LABELS: Record<string, string> = {
  power_tools: "⚡ Elværktøj",
  hand_tools: "🔨 Håndværktøj",
  measuring: "📏 Måling",
  safety: "🦺 Sikkerhed",
  gardening: "🌱 Have",
  materials: "📦 Materialer",
  general: "🔧 Generelt",
};

const STATUS_COLORS: Record<string, string> = {
  available: "bg-green-100 text-green-800",
  lent: "bg-orange-100 text-orange-800",
  reserved: "bg-yellow-100 text-yellow-800",
  maintenance: "bg-red-100 text-red-800",
};

const STATUS_LABELS: Record<string, string> = {
  available: "Tilgængelig",
  lent: "Udlånt",
  reserved: "Reserveret",
  maintenance: "Service",
};

// ─────────────────────────────────────────────────────────────────────────────
// API helpers
// ─────────────────────────────────────────────────────────────────────────────

async function apiGet(path: string) {
  const r = await fetch(`/api/toollib/${path}`);
  if (!r.ok) throw new Error(`API ${r.status}`);
  return r.json();
}

/** POST → get job_id, then poll until done. Timeout in ms (default 10 min). */
async function apiPostJob(path: string, body: unknown, timeoutMs = 600_000): Promise<unknown> {
  const { job_id } = await apiPost(path, body, 15_000);
  const deadline = Date.now() + timeoutMs;
  while (Date.now() < deadline) {
    await new Promise((r) => setTimeout(r, 2000));
    const job = await apiGet(`ai/job/${job_id}`);
    if (job.status === "done") return job.result;
    if (job.status === "error") throw new Error(job.error || "AI-fejl");
  }
  throw new Error("AI-timeout");
}
async function apiPost(path: string, body: unknown, timeoutMs?: number) {
  const controller = timeoutMs ? new AbortController() : null;
  const timer = controller ? setTimeout(() => controller.abort(), timeoutMs) : null;
  try {
    const r = await fetch(`/api/toollib/${path}`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
      signal: controller?.signal,
    });
    if (!r.ok) {
      const err = await r.json().catch(() => ({}));
      throw new Error(err.detail || `API ${r.status}`);
    }
    return r.json();
  } finally {
    if (timer) clearTimeout(timer);
  }
}
async function apiPostForm(path: string, form: FormData) {
  const r = await fetch(`/api/toollib/${path}`, { method: "POST", body: form });
  if (!r.ok) throw new Error(`API ${r.status}`);
  return r.json();
}

// ─────────────────────────────────────────────────────────────────────────────
// Availability timeline bar (30-day view)
// ─────────────────────────────────────────────────────────────────────────────

function AvailabilityBar({ reservations }: { reservations: ReservationSlot[] }) {
  const DAYS = 30;
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const segments = Array.from({ length: DAYS }, (_, i) => {
    const day = new Date(today);
    day.setDate(today.getDate() + i);
    const ts = day.getTime();
    return reservations.some((r) => {
      const s = new Date(r.start_date).getTime();
      const e = new Date(r.end_date).getTime();
      return ts >= s && ts < e;
    });
  });

  const hasAny = segments.some(Boolean);
  if (!hasAny && reservations.length === 0) return null;

  // Find next reservation start for text label
  const upcoming = reservations
    .map((r) => ({ text: r.start_date, d: new Date(r.start_date) }))
    .filter((r) => r.d >= today)
    .sort((a, b) => a.d.getTime() - b.d.getTime())[0];

  return (
    <div className="mt-2 px-0.5">
      <div className="flex gap-px">
        {segments.map((booked, i) => (
          <div
            key={i}
            className={`flex-1 h-1.5 rounded-sm ${
              booked ? "bg-orange-400" : "bg-green-200"
            }`}
            title={`${new Date(today.getTime() + i * 86400000).toLocaleDateString("da-DK")}${
              booked ? " – optaget" : ""
            }`}
          />
        ))}
      </div>
      <div className="flex justify-between mt-0.5">
        <span className="text-[9px] text-gray-400">I dag</span>
        {upcoming && (
          <span className="text-[9px] text-orange-600">
            Optaget fra {new Date(upcoming.d).toLocaleDateString("da-DK", { day: "numeric", month: "short" })}
          </span>
        )}
        <span className="text-[9px] text-gray-400">+30 dage</span>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Tool card
// ─────────────────────────────────────────────────────────────────────────────

function ToolCard({
  tool,
  checked,
  onToggle,
  showCheckbox = false,
  reservations = [],
}: {
  tool: Tool;
  checked?: boolean;
  onToggle?: (uuid: string) => void;
  showCheckbox?: boolean;
  reservations?: ReservationSlot[];
}) {
  return (
    <div
      className={`relative bg-white rounded-xl border-2 transition-all cursor-default ${
        checked ? "border-green-500 shadow-md shadow-green-100" : "border-gray-200 hover:border-gray-300"
      }`}
      onClick={() => onToggle?.(tool.uuid)}
    >
      {showCheckbox && (
        <div className="absolute top-3 right-3 z-10">
          <div className={`w-5 h-5 rounded border-2 flex items-center justify-center ${
            checked ? "bg-green-500 border-green-500" : "bg-white border-gray-300"
          }`}>
            {checked && <span className="text-white text-xs font-bold">✓</span>}
          </div>
        </div>
      )}

      {/* Image / placeholder */}
      <div className="h-36 bg-gradient-to-br from-gray-100 to-gray-200 rounded-t-xl flex items-center justify-center overflow-hidden">
        {tool.image_url ? (
          <img src={tool.image_url} alt={tool.name} className="w-full h-full object-cover" />
        ) : (
          <span className="text-5xl">{CATEGORY_LABELS[tool.category]?.split(" ")[0] ?? "🔧"}</span>
        )}
      </div>

      <div className="p-3">
        <div className="flex items-start justify-between gap-1 mb-1">
          <h3 className="font-semibold text-gray-900 text-sm leading-tight">{tool.name}</h3>
          <span className={`text-xs px-1.5 py-0.5 rounded-full font-medium flex-shrink-0 ${STATUS_COLORS[tool.status]}`}>
            {STATUS_LABELS[tool.status]}
          </span>
        </div>
        <p className="text-xs text-gray-500 mb-2 line-clamp-2">{tool.description || "Ingen beskrivelse"}</p>
        <div className="flex items-center justify-between">
          <span className="text-xs text-gray-400">{CATEGORY_LABELS[tool.category] ?? tool.category}</span>
          {tool.daily_rate > 0 ? (
            <span className="text-xs font-medium text-blue-700">{tool.daily_rate} kr/dag</span>
          ) : (
            <span className="text-xs text-green-600 font-medium">Gratis m. abo.</span>
          )}
        </div>
        {tool.requires_training && (
          <div className="mt-1.5 text-xs bg-amber-50 text-amber-700 rounded px-2 py-0.5 inline-block">
            ⚠️ Kræver oplæring
          </div>
        )}

        {/* Community pickup location info for donated items */}
        {tool.is_free && tool.pickup_preference === "pickup" && tool.pickup_address && (
          <div className="mt-2 rounded-lg bg-blue-50 border border-blue-100 px-2 py-1.5 space-y-0.5">
            <p className="text-xs font-semibold text-blue-800">📍 Lokal afhentning</p>
            <p className="text-xs text-blue-700">{tool.pickup_address}</p>
            {tool.pickup_time_span && (
              <p className="text-xs text-blue-600">🕐 {tool.pickup_time_span}</p>
            )}
            {tool.pickup_instructions && (
              <p className="text-xs text-blue-600 italic">{tool.pickup_instructions}</p>
            )}
          </div>
        )}

        {/* Reservation timeline */ }
        {reservations.length > 0 && <AvailabilityBar reservations={reservations} />}

        {/* Next-free hint for lent/reserved tools */}
        {(tool.status === "lent" || tool.status === "reserved") && reservations.length > 0 && (() => {
          const now = new Date();
          const futureEnds = reservations
            .map((r) => new Date(r.end_date))
            .filter((d) => d > now)
            .sort((a, b) => b.getTime() - a.getTime());
          const latestEnd = futureEnds[0];
          return latestEnd ? (
            <p className="text-[10px] text-orange-700 mt-1 font-medium">
              Ledig fra: {latestEnd.toLocaleDateString("da-DK", { day: "numeric", month: "short" })}
            </p>
          ) : null;
        })()}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Browse tab
// ─────────────────────────────────────────────────────────────────────────────

function BrowseTab({ tools, onAddToBooking }: { tools: Tool[]; onAddToBooking: (uuids: string[]) => void }) {
  const [search, setSearch] = useState("");
  const [catFilter, setCatFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [filterDate, setFilterDate] = useState("");
  const [dateAvailUuids, setDateAvailUuids] = useState<Set<string> | null>(null);
  const [dateLoading, setDateLoading] = useState(false);
  const [calendar, setCalendar] = useState<Record<string, ReservationSlot[]>>({});

  // Fetch upcoming reservation calendar once on mount
  useEffect(() => {
    apiGet("bookings/calendar?days=60")
      .then((res) => setCalendar((res as { calendar: Record<string, ReservationSlot[]> }).calendar ?? {}))
      .catch(() => {/* non-critical */});
  }, []);

  // Fetch availability for the chosen date whenever it changes
  useEffect(() => {
    if (!filterDate) { setDateAvailUuids(null); return; }
    let cancelled = false;
    setDateLoading(true);
    const params = new URLSearchParams({
      status: "available",
      available_from: new Date(filterDate).toISOString(),
      available_to: new Date(filterDate + "T23:59:59").toISOString(),
    });
    apiGet(`tools?${params.toString()}`)
      .then((res) => {
        if (!cancelled) {
          const uuids = new Set(((res as { tools?: Tool[] }).tools ?? []).map((t) => t.uuid));
          setDateAvailUuids(uuids);
        }
      })
      .catch(() => { if (!cancelled) setDateAvailUuids(null); })
      .finally(() => { if (!cancelled) setDateLoading(false); });
    return () => { cancelled = true; };
  }, [filterDate]);

  const filtered = tools.filter((t) => {
    if (catFilter !== "all" && t.category !== catFilter) return false;
    if (statusFilter !== "all" && t.status !== statusFilter) return false;
    if (filterDate && dateAvailUuids !== null && !dateAvailUuids.has(t.uuid)) return false;
    if (search) {
      const q = search.toLowerCase();
      return t.name.toLowerCase().includes(q) || t.description.toLowerCase().includes(q);
    }
    return true;
  });

  const toggle = (uuid: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(uuid)) next.delete(uuid);
      else next.add(uuid);
      return next;
    });
  };

  const categories = Array.from(new Set(tools.map((t) => t.category)));

  return (
    <div className="flex flex-col h-full">
      {/* Filters */}
      <div className="flex flex-wrap gap-2 mb-4">
        <input
          type="text"
          placeholder="🔍 Søg efter værktøj..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="flex-1 min-w-48 px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:border-green-400"
        />
        <select
          value={catFilter}
          onChange={(e) => setCatFilter(e.target.value)}
          className="px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none"
        >
          <option value="all">Alle kategorier</option>
          {categories.map((c) => <option key={c} value={c}>{CATEGORY_LABELS[c] ?? c}</option>)}
        </select>
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none"
        >
          <option value="all">Alle statuser</option>
          <option value="available">Tilgængelig</option>
          <option value="lent">Udlånt</option>
        </select>
        <div className="flex items-center gap-1">
          <label className="text-xs text-gray-500 whitespace-nowrap">
            {dateLoading ? "⏳" : "📅"} Ledig den:
          </label>
          <input
            type="date"
            value={filterDate}
            onChange={(e) => setFilterDate(e.target.value)}
            className={`px-2 py-2 text-sm border rounded-lg focus:outline-none ${
              filterDate ? "border-green-400 bg-green-50" : "border-gray-200"
            }`}
          />
          {filterDate && (
            <button
              onClick={() => { setFilterDate(""); setDateAvailUuids(null); }}
              className="text-xs text-gray-400 hover:text-gray-600 px-1"
              title="Nulstil datofilter"
            >
              ✕
            </button>
          )}
        </div>
      </div>

      {/* Action bar */}
      {selected.size > 0 && (
        <div className="mb-4 flex items-center justify-between bg-green-50 border border-green-200 rounded-xl px-4 py-3">
          <span className="text-sm text-green-800 font-medium">
            {selected.size} værktøj(er) valgt
          </span>
          <button
            onClick={() => { onAddToBooking(Array.from(selected)); setSelected(new Set()); }}
            className="text-sm px-4 py-2 bg-green-700 text-white rounded-lg hover:bg-green-800"
          >
            Book valgte →
          </button>
        </div>
      )}

      {/* Grid */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3 overflow-y-auto flex-1">
        {filtered.length === 0 && (
          <div className="col-span-full text-center py-16 text-gray-400">
            <div className="text-4xl mb-3">🔧</div>
            <p>Ingen værktøjer matcher søgningen</p>
          </div>
        )}
        {filtered.map((t) => (
          <ToolCard
            key={t.uuid}
            tool={t}
            checked={selected.has(t.uuid)}
            onToggle={t.status === "available" ? toggle : undefined}
            showCheckbox={t.status === "available"}
            reservations={calendar[t.uuid] ?? []}
          />
        ))}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// AI Recommend tab
// ─────────────────────────────────────────────────────────────────────────────

function AIRecommendTab({ tools, onAddToBooking }: { tools: Tool[]; onAddToBooking: (uuids: string[]) => void }) {
  const [phase, setPhase] = useState<"input" | "clarify" | "results">("input");

  // Phase 1 — input
  const [description, setDescription] = useState("");
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [cachedImageB64, setCachedImageB64] = useState<string | null>(null);

  // Phase 2 — clarify
  const [imageDescription, setImageDescription] = useState("");
  const [questions, setQuestions] = useState<{ id: string; question: string }[]>([]);
  const [userAnswers, setUserAnswers] = useState<Record<string, string>>({});

  // Phase 3 — results
  const [recommendations, setRecommendations] = useState<Recommendation[]>([]);
  const [selectedRecs, setSelectedRecs] = useState<Set<string>>(new Set());
  const [jobContext, setJobContext] = useState("");

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [cameraOpen, setCameraOpen] = useState(false);
  const streamRef = useRef<MediaStream | null>(null);

  const handleImageChange = (file: File) => {
    setImageFile(file);
    const reader = new FileReader();
    reader.onload = (e) => setImagePreview(e.target?.result as string);
    reader.readAsDataURL(file);
  };

  const openCamera = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: "environment" }, audio: false });
      streamRef.current = stream;
      if (videoRef.current) videoRef.current.srcObject = stream;
      setCameraOpen(true);
    } catch {
      setError("Kamera ikke tilgængeligt");
    }
  };

  const capturePhoto = () => {
    if (!videoRef.current || !canvasRef.current) return;
    const ctx = canvasRef.current.getContext("2d")!;
    canvasRef.current.width = videoRef.current.videoWidth;
    canvasRef.current.height = videoRef.current.videoHeight;
    ctx.drawImage(videoRef.current, 0, 0);
    canvasRef.current.toBlob((blob) => {
      if (blob) handleImageChange(new File([blob], "capture.jpg", { type: "image/jpeg" }));
    }, "image/jpeg", 0.9);
    streamRef.current?.getTracks().forEach((t) => t.stop());
    setCameraOpen(false);
  };

  const closeCamera = () => {
    streamRef.current?.getTracks().forEach((t) => t.stop());
    setCameraOpen(false);
  };

  /** Compress + resize image to base64 (max 1024px, JPEG q0.75) */
  const toBase64 = (file: File): Promise<string> =>
    new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => {
        const MAX = 1024;
        const scale = Math.min(1, MAX / Math.max(img.width, img.height));
        const w = Math.round(img.width * scale);
        const h = Math.round(img.height * scale);
        const canvas = document.createElement("canvas");
        canvas.width = w;
        canvas.height = h;
        canvas.getContext("2d")!.drawImage(img, 0, 0, w, h);
        resolve(canvas.toDataURL("image/jpeg", 0.75).split(",")[1]);
      };
      img.onerror = reject;
      img.src = URL.createObjectURL(file);
    });

  /** Phase 1 → 2: get clarifying questions */
  const submitForClarify = async () => {
    if (!description.trim()) { setError("Beskriv venligst opgaven"); return; }
    setLoading(true);
    setError("");
    try {
      let image_b64: string | null = null;
      if (imageFile) {
        image_b64 = await toBase64(imageFile);
        setCachedImageB64(image_b64);
      } else {
        setCachedImageB64(null);
      }
      const result = await apiPostJob("ai/clarify", { job_description: description, image_b64 }) as { image_description?: string; questions?: { id: string; question: string }[] };
      setImageDescription(result.image_description || "");
      setQuestions(result.questions || []);
      setUserAnswers({});
      setPhase("clarify");
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : "AI-fejl");
    } finally {
      setLoading(false);
    }
  };

  /** Phase 2 → 3: get recommendations using answers */
  const submitForRecommend = async () => {
    setLoading(true);
    setError("");
    setRecommendations([]);
    try {
      const answers = questions.map((q) => ({ question: q.question, answer: userAnswers[q.id] || "" }));
      const result = await apiPostJob("ai/recommend", {
        job_description: description,
        image_b64: cachedImageB64,
        answers,
      }) as { recommendations?: Recommendation[]; job_context?: string };
      setRecommendations(result.recommendations || []);
      setJobContext(result.job_context || "");
      setSelectedRecs(new Set(result.recommendations?.filter((r: Recommendation) => r.status === "available").map((r: Recommendation) => r.uuid) || []));
      setPhase("results");
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : "AI-fejl");
    } finally {
      setLoading(false);
    }
  };

  const toggleRec = (uuid: string) => {
    setSelectedRecs((prev) => { const n = new Set(prev); if (n.has(uuid)) n.delete(uuid); else n.add(uuid); return n; });
  };

  const resetToInput = () => {
    setPhase("input");
    setRecommendations([]);
    setJobContext("");
    setError("");
  };

  return (
    <div className="space-y-5">
      <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
        <h3 className="font-semibold text-blue-900 mb-1">🤖 AI Anbefalinger</h3>
        <p className="text-sm text-blue-700">Beskriv dit projekt og upload evt. et billede – AI finder de rette værktøjer fra biblioteket.</p>
      </div>

      {/* ── Phase 1: Input ── */}
      {phase === "input" && (
        <>
          <div>
            <label className="text-sm font-medium text-gray-700 mb-1 block">Hvad skal laves?</label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Eks: Jeg skal montere køkkenreoler på en mursteensvæg og bore huller til rørgennemføringer..."
              className="w-full px-3 py-2.5 text-sm border border-gray-200 rounded-xl focus:outline-none focus:border-blue-400 resize-none"
              rows={4}
            />
          </div>

          <div>
            <label className="text-sm font-medium text-gray-700 mb-2 block">Billede af opgaven (valgfrit)</label>
            <div className="flex gap-2 flex-wrap">
              <label className="cursor-pointer px-3 py-2 text-sm border border-gray-200 rounded-lg hover:bg-gray-50 flex items-center gap-1.5">
                📂 Upload billede
                <input type="file" accept="image/*" className="hidden" onChange={(e) => e.target.files?.[0] && handleImageChange(e.target.files[0])} />
              </label>
              <button onClick={openCamera} className="px-3 py-2 text-sm border border-gray-200 rounded-lg hover:bg-gray-50">
                📷 Kamera
              </button>
              {imagePreview && (
                <button onClick={() => { setImageFile(null); setImagePreview(null); }} className="px-3 py-2 text-sm border border-red-200 text-red-600 rounded-lg hover:bg-red-50">
                  ✕ Fjern
                </button>
              )}
            </div>

            {cameraOpen && (
              <div className="mt-3 rounded-xl overflow-hidden border border-gray-200 relative">
                <video ref={videoRef} autoPlay playsInline className="w-full max-h-56 object-cover" />
                <canvas ref={canvasRef} className="hidden" />
                <div className="absolute bottom-3 inset-x-0 flex justify-center gap-3">
                  <button onClick={capturePhoto} className="px-4 py-2 bg-white rounded-full shadow text-sm font-medium">📸 Tag billede</button>
                  <button onClick={closeCamera} className="px-4 py-2 bg-white rounded-full shadow text-sm font-medium text-gray-500">✕</button>
                </div>
              </div>
            )}

            {imagePreview && !cameraOpen && (
              <div className="mt-3 rounded-xl overflow-hidden border border-gray-200">
                <img src={imagePreview} alt="preview" className="w-full max-h-48 object-cover" />
              </div>
            )}
          </div>

          {error && <p className="text-sm text-red-600 bg-red-50 rounded-lg px-3 py-2">{error}</p>}

          <button
            onClick={submitForClarify}
            disabled={loading || !description.trim()}
            className="w-full py-3 rounded-xl text-white font-medium disabled:opacity-50 disabled:cursor-not-allowed"
            style={{ backgroundColor: "#2d6a4f" }}
          >
            {loading ? "⏳ AI analyserer..." : "➡ Fortsæt"}
          </button>
        </>
      )}

      {/* ── Phase 2: Clarifying questions ── */}
      {phase === "clarify" && (
        <>
          <button onClick={resetToInput} className="text-sm text-gray-500 hover:text-gray-700 flex items-center gap-1">
            ← Tilbage
          </button>

          {imageDescription && (
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-3">
              <p className="text-xs font-medium text-amber-800 mb-1">📷 AI ser:</p>
              <p className="text-sm text-amber-900 italic">{imageDescription}</p>
            </div>
          )}

          <div>
            <h4 className="font-semibold text-gray-900 mb-1">Et par hurtige spørgsmål</h4>
            <p className="text-xs text-gray-500 mb-4">AI har brug for lidt mere info for at give præcise anbefalinger. Svar på det du ved.</p>
            <div className="space-y-4">
              {questions.map((q) => (
                <div key={q.id}>
                  <label className="text-sm font-medium text-gray-700 block mb-1">{q.question}</label>
                  <textarea
                    value={userAnswers[q.id] || ""}
                    onChange={(e) => setUserAnswers((prev) => ({ ...prev, [q.id]: e.target.value }))}
                    className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:border-blue-400 resize-none"
                    rows={2}
                    placeholder="Dit svar..."
                  />
                </div>
              ))}
            </div>
          </div>

          {error && <p className="text-sm text-red-600 bg-red-50 rounded-lg px-3 py-2">{error}</p>}

          <button
            onClick={submitForRecommend}
            disabled={loading}
            className="w-full py-3 rounded-xl text-white font-medium disabled:opacity-50 disabled:cursor-not-allowed"
            style={{ backgroundColor: "#2d6a4f" }}
          >
            {loading ? "⏳ AI finder værktøjer..." : "🔍 Find anbefalede værktøjer"}
          </button>
        </>
      )}

      {/* ── Phase 3: Results ── */}
      {phase === "results" && (
        <>
          <button onClick={() => setPhase("clarify")} className="text-sm text-gray-500 hover:text-gray-700 flex items-center gap-1">
            ← Tilbage til spørgsmål
          </button>

          {recommendations.length > 0 ? (
            <div>
              {jobContext && (
                <div className="text-xs text-gray-500 bg-gray-50 rounded-lg p-3 mb-3 line-clamp-3">{jobContext}</div>
              )}
              <div className="flex items-center justify-between mb-3">
                <h4 className="font-semibold text-gray-900">Anbefalede værktøjer ({recommendations.length})</h4>
                {selectedRecs.size > 0 && (
                  <button
                    onClick={() => onAddToBooking(Array.from(selectedRecs))}
                    className="text-sm px-4 py-1.5 bg-green-700 text-white rounded-lg"
                  >
                    Book {selectedRecs.size} valgte →
                  </button>
                )}
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                {recommendations.map((rec) => {
                  const full = tools.find((t) => t.uuid === rec.uuid);
                  return (
                    <div
                      key={rec.uuid}
                      onClick={() => rec.status === "available" && toggleRec(rec.uuid)}
                      className={`rounded-xl border-2 p-3 cursor-pointer transition-all ${
                        selectedRecs.has(rec.uuid) ? "border-green-500 bg-green-50" : "border-gray-200 bg-white"
                      }`}
                    >
                      <div className="flex justify-between items-start mb-1">
                        <span className="font-medium text-sm text-gray-900">{rec.name}</span>
                        <span className={`text-xs px-1.5 py-0.5 rounded-full ${STATUS_COLORS[rec.status]}`}>
                          {STATUS_LABELS[rec.status]}
                        </span>
                      </div>
                      {rec.reason && <p className="text-xs text-blue-700 italic mb-1">💡 {rec.reason}</p>}
                      {full?.daily_rate ? (
                        <p className="text-xs text-gray-500">{full.daily_rate} kr/dag</p>
                      ) : null}
                    </div>
                  );
                })}
              </div>
            </div>
          ) : (
            <div className="text-center py-8 text-gray-400">
              <p className="text-sm">Ingen passende værktøjer fundet i kataloget.</p>
              <button onClick={resetToInput} className="mt-3 text-sm text-blue-600 hover:underline">Prøv igen</button>
            </div>
          )}
        </>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Book tab
// ─────────────────────────────────────────────────────────────────────────────

function BookTab({
  tools,
  preSelected,
  onBooked,
}: {
  tools: Tool[];
  preSelected: string[];
  onBooked: () => void;
}) {
  const [selected, setSelected] = useState<Set<string>>(new Set(preSelected));
  const [userName, setUserName] = useState("");
  const [rfidUid, setRfidUid] = useState("");
  const [dayPassword, setDayPassword] = useState("");
  const [dayPassGroup, setDayPassGroup] = useState<{ group_id: string; group_name: string; pricing_scheme?: string; fixed_price?: number } | null>(null);
  const [dayPassError, setDayPassError] = useState("");
  const [dayPassChecking, setDayPassChecking] = useState(false);
  const [startDate, setStartDate] = useState(() => new Date().toISOString().split("T")[0]);
  const [endDate, setEndDate] = useState(() => {
    const d = new Date(); d.setDate(d.getDate() + 1);
    return d.toISOString().split("T")[0];
  });
  const [billingType, setBillingType] = useState<"single" | "subscription">("single");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState<string | null>(null);
  const [search, setSearch] = useState("");
  const [filterByDate, setFilterByDate] = useState(true);
  const [dateFilteredTools, setDateFilteredTools] = useState<Tool[] | null>(null);
  const [dateLoading, setDateLoading] = useState(false);
  const [calendar, setCalendar] = useState<Record<string, ReservationSlot[]>>({});

  // Fetch calendar once for near-return risk warnings
  useEffect(() => {
    apiGet("bookings/calendar?days=30")
      .then((res) => setCalendar((res as { calendar: Record<string, ReservationSlot[]> }).calendar ?? {}))
      .catch(() => {});
  }, []);

  // Account session from /account page
  const [accountUser, setAccountUser] = useState<string | null>(null);
  useEffect(() => {
    const raw = typeof window !== "undefined" ? localStorage.getItem("account_user") : null;
    if (raw) {
      try { setAccountUser(JSON.parse(raw).username); } catch { /* ignore */ }
    }
    // Pre-fill userName from account if available
    if (raw) {
      try {
        const u = JSON.parse(raw);
        setUserName((prev) => prev || u.username);
      } catch { /* ignore */ }
    }
  }, []);

  useEffect(() => { setSelected(new Set(preSelected)); }, [preSelected]);

  // Check if any selected tool requires an account (paid or subscription)
  const selectedTools_check = tools.filter((t) => selected.has(t.uuid));
  const hasPaidTools = selectedTools_check.some((t) => t.daily_rate > 0 || t.subscription_included);
  const needsAccount = hasPaidTools && !accountUser;

  // Fetch tools available for the selected date range whenever dates or toggle change
  useEffect(() => {
    if (!filterByDate || !startDate || !endDate) {
      setDateFilteredTools(null);
      return;
    }
    let cancelled = false;
    setDateLoading(true);
    const params = new URLSearchParams({
      status: "available",
      available_from: new Date(startDate + "T15:00:00").toISOString(),
      available_to: new Date(endDate + "T12:00:00").toISOString(),
    });
    apiGet(`tools?${params.toString()}`)
      .then((res) => { if (!cancelled) setDateFilteredTools((res as { tools?: Tool[] }).tools ?? []); })
      .catch(() => { if (!cancelled) setDateFilteredTools(null); })
      .finally(() => { if (!cancelled) setDateLoading(false); });
    return () => { cancelled = true; };
  }, [filterByDate, startDate, endDate]);

  // Debounced day-password validation
  useEffect(() => {
    if (!dayPassword.trim()) {
      setDayPassGroup(null);
      setDayPassError("");
      return;
    }
    const timer = setTimeout(async () => {
      setDayPassChecking(true);
      setDayPassError("");
      try {
        const res = await fetch("/api/toollib/bookings/validate-daypass", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ password: dayPassword }),
        });
        if (res.ok) {
          const data = await res.json();
          setDayPassGroup(data);
        } else {
          setDayPassGroup(null);
          setDayPassError("Ugyldig kode for i dag");
        }
      } catch {
        setDayPassGroup(null);
        setDayPassError("Kunne ikke validere kode");
      } finally {
        setDayPassChecking(false);
      }
    }, 500);
    return () => clearTimeout(timer);
  }, [dayPassword]);

  const toggle = (uuid: string) => {
    setSelected((prev) => { const n = new Set(prev); if (n.has(uuid)) n.delete(uuid); else n.add(uuid); return n; });
  };

  // Use date-filtered list when available, otherwise fall back to the full prop
  const sourceTools = filterByDate && dateFilteredTools !== null ? dateFilteredTools : tools;
  const available = sourceTools.filter((t) => t.status === "available");
  const filtered = available.filter((t) =>
    !search || t.name.toLowerCase().includes(search.toLowerCase()),
  );

  const days = Math.max(1, Math.ceil(
    (new Date(endDate + "T12:00:00").getTime() - new Date(startDate + "T15:00:00").getTime()) / 86400000
  ));
  const selectedTools = tools.filter((t) => selected.has(t.uuid));

  // Near-return risk: selected tools with a booking ending within 2 days before the new startDate
  const nearReturnWarnings = useMemo(() => {
    if (!startDate) return [];
    const pickupTime = new Date(startDate + "T15:00:00");
    const twoDaysBefore = new Date(pickupTime.getTime() - 2 * 86400000);
    return selectedTools.filter((t) => {
      const reservations = calendar[t.uuid] ?? [];
      return reservations.some((r) => {
        const end = new Date(r.end_date);
        return end >= twoDaysBefore && end <= pickupTime;
      });
    });
  }, [selectedTools, calendar, startDate]);

  // Pricing logic: group can override non-material tools with a flat price
  const isMaterial = (t: Tool) => t.item_type === "material";
  const materialCost = selectedTools.filter(isMaterial).reduce((s, t) => s + t.daily_rate * days, 0);
  const toolsCost = selectedTools.filter((t) => !isMaterial(t)).reduce((s, t) => s + t.daily_rate * days, 0);
  const groupPricingScheme = dayPassGroup?.pricing_scheme ?? "per_tool_daily";
  const groupFixedPrice = dayPassGroup?.fixed_price ?? 0;
  const totalCost = groupPricingScheme === "fixed_all"
    ? groupFixedPrice + materialCost
    : toolsCost + materialCost;

  const submit = async () => {
    if (!userName.trim()) { setError("Navn er påkrævet"); return; }
    if (selected.size === 0) { setError("Vælg mindst ét værktøj"); return; }
    if (billingType === "single" && days > 3) { setError("Single lån max 3 dage"); return; }
    if (needsAccount) { setError("Du skal logge ind på din konto for at booke betalte værktøjer"); return; }
    setLoading(true); setError(""); setSuccess(null);
    try {
      const booking = await apiPost("bookings", {
        tool_uuids: Array.from(selected),
        user_name: userName,
        rfid_uid: rfidUid || undefined,
        day_password: dayPassword || undefined,
        start_date: new Date(startDate + "T15:00:00").toISOString(),
        end_date: new Date(endDate + "T12:00:00").toISOString(),
        billing_type: billingType,
      });
      setSuccess(`Booking oprettet! ID: ${booking.booking_id}${dayPassGroup ? ` (Gruppe: ${dayPassGroup.group_name})` : ""}`);
      setSelected(new Set());
      setDayPassword("");
      setDayPassGroup(null);
      onBooked();
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : "Fejl ved booking");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex gap-4 h-full">
      {/* Left: tool selection grid */}
      <div className="flex-1 flex flex-col min-w-0">
        <div className="flex items-center gap-2 mb-3">
          <input
            type="text"
            placeholder="🔍 Filtrer..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="flex-1 px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none"
          />
          <button
            onClick={() => setFilterByDate((v) => !v)}
            title={filterByDate ? "Filtrer efter valgte datoer (aktiveret)" : "Vis alle uanset datoer"}
            className={`flex-shrink-0 flex items-center gap-1.5 px-3 py-2 text-xs rounded-lg border transition-colors ${
              filterByDate
                ? "bg-green-700 text-white border-green-700"
                : "bg-white text-gray-600 border-gray-200 hover:bg-gray-50"
            }`}
          >
            {dateLoading ? "⏳" : "📅"} Ledige datoer
          </button>
          <span className="text-xs text-gray-500 flex-shrink-0">{selected.size} valgt</span>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2.5 overflow-y-auto flex-1">
          {filterByDate && dateFilteredTools !== null && (
            <div className="col-span-full mb-1 px-2 py-1.5 bg-green-50 border border-green-200 rounded-lg text-xs text-green-800">
              📅 Viser kun ledige {startDate === endDate ? `den ${startDate}` : `${startDate} – ${endDate}`} ({filtered.length} tilgængelige)
            </div>
          )}
          {filtered.map((t) => (
            <ToolCard key={t.uuid} tool={t} checked={selected.has(t.uuid)} onToggle={toggle} showCheckbox />
          ))}
          {filtered.length === 0 && (
            <div className="col-span-full text-center py-8 text-gray-400 text-sm">Ingen tilgængelige værktøjer</div>
          )}
        </div>
      </div>

      {/* Right: booking form */}
      <div className="w-72 flex-shrink-0 flex flex-col gap-3">
        <div className="bg-white rounded-xl border border-gray-200 p-4 flex-1 overflow-y-auto space-y-3">
          <h3 className="font-semibold text-gray-900">Booking detaljer</h3>

          {/* Account status / login prompt */}
          {accountUser ? (
            <div className="flex items-center gap-2 bg-green-50 border border-green-200 rounded-lg px-2.5 py-1.5">
              <span className="text-green-700 text-xs">✅ Logget ind som <strong>{accountUser}</strong></span>
              <a href="/account" className="ml-auto text-xs text-green-700 hover:underline">Min konto →</a>
            </div>
          ) : needsAccount ? (
            <a href="/account" className="block bg-orange-50 border border-orange-200 rounded-lg px-2.5 py-2 text-xs text-orange-800 hover:bg-orange-100 transition-colors">
              ⚠️ Log ind for at booke betalte og abonnements-værktøjer →
            </a>
          ) : null}

          <div>
            <label className="text-xs text-gray-600 font-medium">Navn *</label>
            <input value={userName} onChange={(e) => setUserName(e.target.value)}
              className="w-full mt-0.5 px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:border-green-400"
              placeholder="Dit fulde navn" />
          </div>

          <div>
            <label className="text-xs text-gray-600 font-medium">RFID UID (valgfrit)</label>
            <input value={rfidUid} onChange={(e) => setRfidUid(e.target.value)}
              className="w-full mt-0.5 px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none"
              placeholder="Kortnummer" />
          </div>

          <div>
            <label className="text-xs text-gray-600 font-medium">Gruppe adgangskode (valgfrit)</label>
            <div className="relative mt-0.5">
              <input
                value={dayPassword}
                onChange={(e) => setDayPassword(e.target.value)}
                className={`w-full px-3 py-2 text-sm border rounded-lg focus:outline-none pr-8 ${
                  dayPassGroup ? "border-green-400 bg-green-50" :
                  dayPassError ? "border-red-300" : "border-gray-200"
                }`}
                placeholder="Dagens kode"
                autoComplete="off"
              />
              <span className="absolute right-2 top-2.5 text-sm">
                {dayPassChecking ? "⏳" : dayPassGroup ? "✅" : dayPassError ? "❌" : ""}
              </span>
            </div>
            {dayPassGroup && (
              <div className="mt-0.5">
                <p className="text-xs text-green-700 font-medium">Gruppe: {dayPassGroup.group_name}</p>
                <p className="text-xs text-blue-600 mt-0.5">
                  {dayPassGroup.pricing_scheme === "fixed_all"
                    ? `💶 Fast pris: ${dayPassGroup.fixed_price ?? 0} kr (materialer pr. forbrug)`
                    : "💶 Per redskab/dag"}
                </p>
              </div>
            )}
            {dayPassError && (
              <p className="text-xs text-red-600 mt-0.5">{dayPassError}</p>
            )}
          </div>

          <div className="flex gap-2">
            <div className="flex-1">
              <label className="text-xs text-gray-600 font-medium">Fra</label>
              <input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)}
                className="w-full mt-0.5 px-2 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none" />
            </div>
            <div className="flex-1">
              <label className="text-xs text-gray-600 font-medium">Til</label>
              <input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)}
                className="w-full mt-0.5 px-2 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none" />
            </div>
          </div>

          <div>
            <label className="text-xs text-gray-600 font-medium">Faktureringstype</label>
            <div className="mt-1 flex rounded-lg border border-gray-200 overflow-hidden text-xs">
              {(["single", "subscription"] as const).map((t) => (
                <button key={t} onClick={() => setBillingType(t)}
                  className={`flex-1 py-2 transition-colors ${billingType === t ? "bg-green-700 text-white font-medium" : "bg-white text-gray-600"}`}>
                  {t === "single" ? "Enkelt lån" : "Abonnement"}
                </button>
              ))}
            </div>
            {billingType === "single" && (
              <p className="text-xs text-gray-400 mt-1">Max 3 dage. {days} dag(e) valgt.</p>
            )}
          </div>

          {/* Summary */}
          {selectedTools.length > 0 && (
            <div className="bg-gray-50 rounded-lg p-3 text-xs space-y-1">
              <p className="font-medium text-gray-700">Valgte værktøjer:</p>
              {groupPricingScheme === "fixed_all" ? (
                <>
                  {/* Tools — covered by flat group price */}
                  {selectedTools.filter((t) => !isMaterial(t)).map((t) => (
                    <div key={t.uuid} className="flex justify-between text-gray-500">
                      <span className="truncate">{t.name}</span>
                      <span className="flex-shrink-0 ml-1 italic">dækket af fast pris</span>
                    </div>
                  ))}
                  {/* Materials — priced by consumption */}
                  {selectedTools.filter(isMaterial).map((t) => (
                    <div key={t.uuid} className="flex justify-between text-gray-600">
                      <span className="truncate">{t.name} <span className="text-gray-400">(materiale)</span></span>
                      <span className="flex-shrink-0 ml-1">{t.daily_rate > 0 ? `${t.daily_rate * days} kr` : "Gratis"}</span>
                    </div>
                  ))}
                  <div className="flex justify-between text-gray-600 pt-0.5 border-t border-gray-200">
                    <span>Gruppepris (redskaber)</span>
                    <span>{groupFixedPrice} kr</span>
                  </div>
                  {materialCost > 0 && (
                    <div className="flex justify-between text-gray-600">
                      <span>Materialer</span>
                      <span>{materialCost} kr</span>
                    </div>
                  )}
                </>
              ) : (
                selectedTools.map((t) => (
                  <div key={t.uuid} className="flex justify-between text-gray-600">
                    <span className="truncate">{t.name}</span>
                    <span className="flex-shrink-0 ml-1">{t.daily_rate > 0 ? `${t.daily_rate * days} kr` : "Gratis"}</span>
                  </div>
                ))
              )}
              {totalCost > 0 && (
                <div className="flex justify-between font-semibold text-gray-800 pt-1 border-t border-gray-200">
                  <span>Total</span>
                  <span>{totalCost} kr</span>
                </div>
              )}
            </div>
          )}

          {/* Near-return risk warnings */}
          {nearReturnWarnings.length > 0 && (
            <div className="rounded-lg border border-orange-300 bg-orange-50 px-2.5 py-2 space-y-1">
              <p className="text-xs font-semibold text-orange-800">⚠️ Risiko for forsinkelse</p>
              {nearReturnWarnings.map((t) => (
                <p key={t.uuid} className="text-xs text-orange-700">
                  {t.item_type === "material"
                    ? `🧪 ${t.name} — materiale kan være opbrugt ved aflevering`
                    : `🔧 ${t.name} — aflevering inden for 2 dage fra booking`}
                </p>
              ))}
              <p className="text-xs text-orange-600 italic">Afhentning kl. 15:00 · Aflevering senest kl. 12:00</p>
            </div>
          )}

          {error && <p className="text-xs text-red-600 bg-red-50 rounded px-2 py-1.5">{error}</p>}
          {success && <p className="text-xs text-green-700 bg-green-50 rounded px-2 py-1.5">{success}</p>}
        </div>

        <button
          onClick={submit}
          disabled={loading || selected.size === 0}
          className="w-full py-3 rounded-xl text-white font-medium text-sm disabled:opacity-40"
          style={{ backgroundColor: "#2d6a4f" }}
        >
          {loading ? "⏳ Booker..." : `📋 Opret booking (${selected.size})`}
        </button>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// My bookings tab
// ─────────────────────────────────────────────────────────────────────────────

function MyBookingsTab({ tools }: { tools: Tool[] }) {
  const [rfidUid, setRfidUid] = useState("");
  const [bookings, setBookings] = useState<Booking[]>([]);
  const [loading, setLoading] = useState(false);
  const [returning, setReturning] = useState<string | null>(null);
  const [error, setError] = useState("");

  const load = async () => {
    if (!rfidUid.trim()) { setError("Indtast dit RFID-kortnummer"); return; }
    setLoading(true); setError("");
    try {
      const res = await apiGet(`bookings/my?rfid_uid=${encodeURIComponent(rfidUid)}`);
      setBookings(res.bookings || []);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : "Fejl");
    } finally { setLoading(false); }
  };

  const returnBooking = async (bookingId: string) => {
    setReturning(bookingId);
    try {
      await apiPost(`bookings/${bookingId}/return`, {});
      setBookings((prev) => prev.map((b) => b.booking_id === bookingId ? { ...b, status: "returned" } : b));
    } catch (e: unknown) {
      alert(e instanceof Error ? e.message : "Fejl ved returnering");
    } finally { setReturning(null); }
  };

  const toolMap = Object.fromEntries(tools.map((t) => [t.uuid, t]));

  const statusColor: Record<string, string> = {
    active: "text-green-700 bg-green-100",
    returned: "text-gray-600 bg-gray-100",
    overdue: "text-red-700 bg-red-100",
    pending: "text-yellow-700 bg-yellow-100",
  };

  return (
    <div className="space-y-4">
      <div className="flex gap-2">
        <input
          type="text"
          value={rfidUid}
          onChange={(e) => setRfidUid(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && load()}
          placeholder="📟 Indtast RFID kortnummer..."
          className="flex-1 px-3 py-2.5 text-sm border border-gray-200 rounded-xl focus:outline-none focus:border-green-400"
        />
        <button
          onClick={load}
          disabled={loading}
          className="px-4 py-2 text-sm text-white rounded-xl font-medium disabled:opacity-50"
          style={{ backgroundColor: "#2d6a4f" }}
        >
          {loading ? "⏳" : "Hent"}
        </button>
      </div>

      {error && <p className="text-sm text-red-600 bg-red-50 rounded-lg px-3 py-2">{error}</p>}

      {bookings.length === 0 && !loading && rfidUid && (
        <div className="text-center py-12 text-gray-400">
          <div className="text-4xl mb-3">📭</div>
          <p>Ingen bookinger fundet</p>
        </div>
      )}

      <div className="space-y-3">
        {bookings.map((b) => (
          <div key={b.booking_id} className="bg-white rounded-xl border border-gray-200 p-4">
            <div className="flex justify-between items-start mb-3">
              <div>
                <p className="text-xs text-gray-400 font-mono">{b.booking_id.slice(0, 12)}…</p>
                <div className="flex items-center gap-2 mt-1">
                  <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${statusColor[b.status] ?? "bg-gray-100 text-gray-600"}`}>
                    {b.status.toUpperCase()}
                  </span>
                  <span className="text-xs text-gray-500">{b.billing_type}</span>
                  {b.odoo_invoice_number && (
                    <span className="text-xs text-blue-600">🧾 {b.odoo_invoice_number}</span>
                  )}
                </div>
              </div>
              {b.status === "active" && (
                <button
                  onClick={() => returnBooking(b.booking_id)}
                  disabled={returning === b.booking_id}
                  className="text-xs px-3 py-1.5 bg-orange-100 text-orange-700 rounded-lg hover:bg-orange-200 disabled:opacity-50"
                >
                  {returning === b.booking_id ? "⏳" : "↩️ Returner"}
                </button>
              )}
            </div>

            <div className="text-xs text-gray-500 mb-2">
              📅 {new Date(b.start_date).toLocaleDateString("da-DK")} → {new Date(b.end_date).toLocaleDateString("da-DK")}
              {b.actual_return_date && (
                <span className="ml-2 text-green-600">Returneret: {new Date(b.actual_return_date).toLocaleDateString("da-DK")}</span>
              )}
            </div>

            <div className="flex flex-wrap gap-1.5">
              {b.tool_uuids.map((uuid) => {
                const t = toolMap[uuid];
                return (
                  <span key={uuid} className="inline-flex items-center gap-1 text-xs bg-gray-100 text-gray-700 rounded-full px-2 py-0.5">
                    🔧 {t?.name ?? uuid.slice(0, 8)}
                  </span>
                );
              })}
            </div>

            {b.total_amount > 0 && (
              <p className="text-xs text-gray-500 mt-2">💰 Total: {b.total_amount} kr</p>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Identify tab (camera or upload → AI identifies tool, check UUID)
// ─────────────────────────────────────────────────────────────────────────────

function IdentifyTab({ tools }: { tools: Tool[] }) {
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<{ vision_description: string; matched_tool: Tool | null; uuid_prefix_guess: string } | null>(null);
  const [error, setError] = useState("");
  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [cameraOpen, setCameraOpen] = useState(false);
  const streamRef = useRef<MediaStream | null>(null);

  const handleFile = (file: File) => {
    setImageFile(file);
    setResult(null);
    const reader = new FileReader();
    reader.onload = (e) => setImagePreview(e.target?.result as string);
    reader.readAsDataURL(file);
  };

  const openCamera = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: "environment" }, audio: false });
      streamRef.current = stream;
      if (videoRef.current) videoRef.current.srcObject = stream;
      setCameraOpen(true);
    } catch { setError("Kamera ikke tilgængeligt"); }
  };

  const capturePhoto = () => {
    if (!videoRef.current || !canvasRef.current) return;
    const ctx = canvasRef.current.getContext("2d")!;
    canvasRef.current.width = videoRef.current.videoWidth;
    canvasRef.current.height = videoRef.current.videoHeight;
    ctx.drawImage(videoRef.current, 0, 0);
    canvasRef.current.toBlob((blob) => {
      if (blob) handleFile(new File([blob], "scan.jpg", { type: "image/jpeg" }));
    }, "image/jpeg", 0.9);
    streamRef.current?.getTracks().forEach((t) => t.stop());
    setCameraOpen(false);
  };

  const identify = async () => {
    if (!imageFile) { setError("Intet billede valgt"); return; }
    setLoading(true); setError("");
    try {
      const form = new FormData();
      form.append("image", imageFile);
      const res = await apiPostForm("ai/identify/upload", form);
      setResult(res);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : "AI-fejl");
    } finally { setLoading(false); }
  };

  return (
    <div className="max-w-lg mx-auto space-y-5">
      <div className="bg-purple-50 border border-purple-200 rounded-xl p-4">
        <h3 className="font-semibold text-purple-900 mb-1">🔍 Identificer værktøj</h3>
        <p className="text-sm text-purple-700">Tag et foto af et værktøj for at slå det op i biblioteket eller bekræfte returnering.</p>
      </div>

      {/* Image capture */}
      <div className="flex gap-2">
        <label className="cursor-pointer px-4 py-2.5 text-sm border border-gray-200 rounded-xl hover:bg-gray-50 flex items-center gap-1.5">
          📂 Upload
          <input type="file" accept="image/*" className="hidden" onChange={(e) => e.target.files?.[0] && handleFile(e.target.files[0])} />
        </label>
        <button onClick={openCamera} className="px-4 py-2.5 text-sm border border-gray-200 rounded-xl hover:bg-gray-50">
          📷 Kamera
        </button>
      </div>

      {cameraOpen && (
        <div className="rounded-xl overflow-hidden border border-gray-200 relative">
          <video ref={videoRef} autoPlay playsInline className="w-full max-h-72 object-cover" />
          <canvas ref={canvasRef} className="hidden" />
          <div className="absolute bottom-3 inset-x-0 flex justify-center gap-3">
            <button onClick={capturePhoto} className="px-4 py-2 bg-white rounded-full shadow text-sm font-medium">📸 Tag billede</button>
            <button onClick={() => { streamRef.current?.getTracks().forEach((t) => t.stop()); setCameraOpen(false); }}
              className="px-4 py-2 bg-white rounded-full shadow text-sm text-gray-500">✕</button>
          </div>
        </div>
      )}

      {imagePreview && !cameraOpen && (
        <img src={imagePreview} alt="scan" className="w-full max-h-64 object-cover rounded-xl border border-gray-200" />
      )}

      {error && <p className="text-sm text-red-600 bg-red-50 rounded-lg px-3 py-2">{error}</p>}

      <button
        onClick={identify}
        disabled={loading || !imageFile}
        className="w-full py-3 rounded-xl text-white font-medium disabled:opacity-40"
        style={{ backgroundColor: "#7c3aed" }}
      >
        {loading ? "⏳ Analyserer..." : "🔍 Identificer"}
      </button>

      {result && (
        <div className="space-y-3">
          <div className="bg-gray-50 rounded-xl p-4">
            <p className="text-xs font-medium text-gray-500 mb-1">AI analyse:</p>
            <p className="text-sm text-gray-800">{result.vision_description}</p>
          </div>
          {result.matched_tool ? (
            <div className="bg-white rounded-xl border-2 border-purple-400 p-4">
              <p className="text-xs text-purple-600 font-medium mb-2">✅ Match fundet i biblioteket</p>
              <ToolCard tool={result.matched_tool} />
              <p className="text-xs text-gray-400 mt-2 font-mono">UUID: {result.matched_tool.uuid}</p>
            </div>
          ) : (
            <div className="bg-orange-50 border border-orange-200 rounded-xl p-4">
              <p className="text-sm text-orange-700">⚠️ Ingen præcis match fundet (prefix: {result.uuid_prefix_guess})</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Main ToolLibrary component
// ─────────────────────────────────────────────────────────────────────────────

type Tab = "browse" | "ai" | "book" | "mybookings" | "identify" | "planner" | "donate_tool" | "donate_materials";

export default function ToolLibrary() {
  const [tools, setTools] = useState<Tool[]>([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState<Tab>("browse");
  const [bookingUuids, setBookingUuids] = useState<string[]>([]);

  const loadTools = useCallback(async () => {
    try {
      const res = await apiGet("tools");
      setTools(res.tools || []);
    } catch {
      /* retry silently */
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadTools(); }, [loadTools]);

  const goBook = (uuids: string[]) => {
    setBookingUuids(uuids);
    setTab("book");
  };

  const tabs: { id: Tab; label: string }[] = [
    { id: "planner", label: "📅 Planlæg" },
    { id: "browse", label: "📦 Katalog" },
    { id: "ai", label: "🤖 AI Anbefaling" },
    { id: "book", label: "📋 Book" },
    { id: "mybookings", label: "📅 Mine bookinger" },
    { id: "identify", label: "🔍 Identificer" },
    { id: "donate_tool", label: "🎁 Donér Værktøj" },
    { id: "donate_materials", label: "♻️ Donér Materialer" },
  ];

  const stats = {
    total: tools.length,
    available: tools.filter((t) => t.status === "available").length,
    lent: tools.filter((t) => t.status === "lent").length,
  };

  return (
    <div className="flex-1 flex flex-col bg-gray-50 min-h-0">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 px-6 py-4">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-wrap items-start justify-between gap-4 mb-4">
            <div>
              <h1 className="text-xl font-bold text-gray-900">🔧 Værktøjs- og Materialebibliotek</h1>
              <p className="text-sm text-gray-500 mt-0.5">Book værktøj, få AI-anbefalinger og administrer udlån</p>
            </div>
            <div className="flex gap-4 text-center">
              <div className="bg-green-50 rounded-xl px-4 py-2">
                <div className="text-xl font-bold text-green-700">{stats.available}</div>
                <div className="text-xs text-green-600">Tilgængelige</div>
              </div>
              <div className="bg-orange-50 rounded-xl px-4 py-2">
                <div className="text-xl font-bold text-orange-700">{stats.lent}</div>
                <div className="text-xs text-orange-600">Udlånt</div>
              </div>
              <div className="bg-gray-100 rounded-xl px-4 py-2">
                <div className="text-xl font-bold text-gray-700">{stats.total}</div>
                <div className="text-xs text-gray-500">I alt</div>
              </div>
            </div>
          </div>

          {/* Tabs */}
          <div className="flex gap-1 overflow-x-auto">
            {tabs.map((t) => (
              <button
                key={t.id}
                onClick={() => setTab(t.id)}
                className={`px-4 py-2 rounded-lg text-sm font-medium whitespace-nowrap transition-colors ${
                  tab === t.id ? "text-white" : "text-gray-600 hover:bg-gray-100"
                }`}
                style={tab === t.id ? { backgroundColor: "#2d6a4f" } : {}}
              >
                {t.label}
                {t.id === "book" && bookingUuids.length > 0 && (
                  <span className="ml-1.5 bg-white text-green-800 text-xs rounded-full px-1.5 py-0.5 font-bold">
                    {bookingUuids.length}
                  </span>
                )}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-hidden">
        <div className="max-w-7xl mx-auto h-full px-4 py-4 overflow-y-auto">
          {loading ? (
            <div className="flex flex-col items-center justify-center py-20 text-gray-400">
              <div className="text-5xl mb-4 animate-spin">⚙️</div>
              <p>Indlæser bibiotek...</p>
            </div>
          ) : (
            <>
              {tab === "browse" && <BrowseTab tools={tools} onAddToBooking={goBook} />}
              {tab === "ai" && <AIRecommendTab tools={tools} onAddToBooking={goBook} />}
              {tab === "book" && <BookTab tools={tools} preSelected={bookingUuids} onBooked={loadTools} />}
              {tab === "mybookings" && <MyBookingsTab tools={tools} />}
              {tab === "identify" && <IdentifyTab tools={tools} />}
              {tab === "planner" && <PlannerTab tools={tools} onAddToBooking={goBook} />}
              {tab === "donate_tool" && <DonateTab itemType="tool" />}
              {tab === "donate_materials" && <DonateTab itemType="material" />}

            </>
          )}
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Planner tab — check multi-tool availability and get alternatives/shop hints
// ─────────────────────────────────────────────────────────────────────────────
function PlannerTab({ tools, onAddToBooking }: { tools: Tool[]; onAddToBooking: (uuids: string[]) => void }) {
  const [selectedUuids, setSelectedUuids] = useState<Set<string>>(new Set());
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [jobDesc, setJobDesc] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<PlannerResponse | null>(null);
  const [error, setError] = useState("");
  const [catFilter, setCatFilter] = useState("all");
  const [search, setSearch] = useState("");

  const categories = useMemo(() => Array.from(new Set(tools.map((t) => t.category).filter(Boolean))).sort() as string[], [tools]);

  const filtered = useMemo(() => {
    let ts = tools.filter((t) => (t.status as string) !== "deleted");
    if (catFilter !== "all") ts = ts.filter((t) => t.category === catFilter);
    if (search.trim()) {
      const q = search.toLowerCase();
      ts = ts.filter((t) => t.name.toLowerCase().includes(q) || (t.description ?? "").toLowerCase().includes(q));
    }
    return ts;
  }, [tools, catFilter, search]);

  const toggle = (uuid: string) => {
    setSelectedUuids((prev) => {
      const next = new Set(prev);
      next.has(uuid) ? next.delete(uuid) : next.add(uuid);
      return next;
    });
  };

  const checkAvailability = async () => {
    if (!selectedUuids.size) return setError("Vælg mindst ét værktøj.");
    if (!startDate || !endDate) return setError("Angiv start- og slutdato.");
    if (new Date(startDate) >= new Date(endDate)) return setError("Slutdato skal være efter startdato.");
    setError("");
    setLoading(true);
    setResult(null);
    try {
      const res = await apiPost("planner", {
        tool_uuids: Array.from(selectedUuids),
        start_date: new Date(startDate + "T15:00:00").toISOString(),
        end_date: new Date(endDate + "T12:00:00").toISOString(),
        job_description: jobDesc,
      });
      setResult(res as PlannerResponse);
    } catch {
      setError("Kunne ikke kontrollere tilgængelighed. Prøv igen.");
    } finally {
      setLoading(false);
    }
  };

  const bookAvailable = () => {
    if (!result) return;
    const availUuids = result.results.filter((r) => r.available).map((r) => r.uuid);
    if (availUuids.length) onAddToBooking(availUuids);
  };

  const moveToCommon = () => {
    if (!result?.common_next_date) return;
    const next = new Date(result.common_next_date);
    const dur = startDate && endDate ? new Date(endDate).getTime() - new Date(startDate).getTime() : 86400000;
    const newEnd = new Date(next.getTime() + dur);
    setStartDate(next.toISOString().slice(0, 10));
    setEndDate(newEnd.toISOString().slice(0, 10));
    setResult(null);
  };

  const fmtDate = (iso: string | null | undefined) => {
    if (!iso) return "–";
    return new Date(iso).toLocaleDateString("da-DK", { day: "numeric", month: "short" });
  };

  return (
    <div className="space-y-5">
      <div className="bg-white rounded-xl border border-gray-200 p-5 space-y-4">
        <h2 className="text-base font-bold text-gray-800">📅 Planlæg booking</h2>

        {/* Date range + description */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Startdato</label>
            <input
              type="date"
              value={startDate}
              onChange={(e) => { setStartDate(e.target.value); setResult(null); }}
              min={new Date().toISOString().slice(0, 10)}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-green-500"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Slutdato</label>
            <input
              type="date"
              value={endDate}
              onChange={(e) => { setEndDate(e.target.value); setResult(null); }}
              min={startDate || new Date().toISOString().slice(0, 10)}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-green-500"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Opgavebeskrivelse (valgfri)</label>
            <input
              type="text"
              value={jobDesc}
              onChange={(e) => setJobDesc(e.target.value)}
              placeholder="F.eks. montere hylde..."
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-green-500"
            />
          </div>
        </div>

        {/* Tool selector */}
        <div>
          <p className="text-xs font-medium text-gray-600 mb-2">Vælg værktøj/materialer</p>
          <div className="flex gap-2 mb-3 flex-wrap">
            <input
              type="text"
              placeholder="Søg..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="border border-gray-300 rounded-lg px-3 py-1.5 text-sm flex-1 min-w-[140px] focus:outline-none focus:ring-2 focus:ring-green-500"
            />
            <select
              value={catFilter}
              onChange={(e) => setCatFilter(e.target.value)}
              className="border border-gray-300 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-green-500"
            >
              <option value="all">Alle kategorier</option>
              {categories.map((c: string) => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2 max-h-60 overflow-y-auto pr-1">
            {filtered.map((t: Tool) => (
              <button
                key={t.uuid}
                onClick={() => { toggle(t.uuid); setResult(null); }}
                className={`flex items-center gap-2 text-left rounded-lg border px-3 py-2 text-sm transition-colors ${
                  selectedUuids.has(t.uuid)
                    ? "bg-green-50 border-green-500 text-green-800"
                    : t.status !== "available"
                    ? "bg-gray-50 border-gray-200 text-gray-400"
                    : "border-gray-200 hover:bg-gray-50 text-gray-700"
                }`}
              >
                <span className={`w-4 h-4 rounded flex-shrink-0 border flex items-center justify-center ${
                  selectedUuids.has(t.uuid) ? "bg-green-600 border-green-600 text-white" : "border-gray-300"
                }`}>
                  {selectedUuids.has(t.uuid) && "✓"}
                </span>
                <span className="truncate font-medium">{t.name}</span>
                <span className={`ml-auto text-[10px] rounded-full px-1.5 py-0.5 flex-shrink-0 ${
                  t.status === "available" ? "bg-green-100 text-green-700" : "bg-orange-100 text-orange-700"
                }`}>
                  {t.status === "available" ? "Ledig" : "Optaget"}
                </span>
              </button>
            ))}
            {filtered.length === 0 && (
              <p className="text-sm text-gray-400 col-span-3 text-center py-4">Ingen matcher søgningen.</p>
            )}
          </div>
          {selectedUuids.size > 0 && (
            <p className="text-xs text-green-700 mt-2">{selectedUuids.size} valgt</p>
          )}
        </div>

        {error && <p className="text-sm text-red-600 bg-red-50 rounded-lg px-3 py-2">{error}</p>}

        <button
          onClick={checkAvailability}
          disabled={loading || !selectedUuids.size || !startDate || !endDate}
          className="w-full py-2.5 rounded-lg text-sm font-semibold text-white transition-opacity disabled:opacity-40"
          style={{ backgroundColor: "#2d6a4f" }}
        >
          {loading ? "Tjekker..." : "Tjek tilgængelighed"}
        </button>
      </div>

      {/* Results */}
      {result && (
        <div className="bg-white rounded-xl border border-gray-200 p-5 space-y-4">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <h3 className="font-bold text-gray-800">
              {result.all_available ? "✅ Alle ledige i perioden" : "⚠️ Ikke alt er ledigt"}
            </h3>
            <span className="text-xs text-gray-500">
              {fmtDate(result.requested.start_date)} → {fmtDate(result.requested.end_date)}
            </span>
          </div>

          {/* Per-tool results */}
          <div className="space-y-3">
            {result.results.map((r) => (
              <div key={r.uuid} className={`rounded-lg border p-3 ${r.available ? "border-green-200 bg-green-50" : "border-orange-200 bg-orange-50"}`}>
                <div className="flex items-start justify-between gap-2">
                  <div>
                    <p className="text-sm font-semibold text-gray-800">{r.available ? "✅" : "⚠️"} {r.name}</p>
                    <p className="text-xs text-gray-500">{r.category}</p>
                  </div>
                  {!r.available && r.next_available_from && (
                    <span className="text-xs bg-orange-100 text-orange-700 rounded-full px-2 py-0.5 whitespace-nowrap">
                      Ledig fra {fmtDate(r.next_available_from)}
                    </span>
                  )}
                </div>

                {/* Blocking periods */}
                {!r.available && r.blocking.length > 0 && (
                  <div className="mt-2 space-y-0.5">
                    {r.blocking.map((b, i) => (
                      <p key={i} className="text-xs text-orange-700">
                        🚫 Optaget: {fmtDate(b.start_date)} → {fmtDate(b.end_date)}
                      </p>
                    ))}
                  </div>
                )}

                {/* Library alternatives */}
                {!r.available && r.alternatives.length > 0 && (
                  <div className="mt-2">
                    <p className="text-xs font-medium text-gray-700 mb-1">Alternativer fra biblioteket:</p>
                    <div className="flex flex-wrap gap-2">
                      {r.alternatives.map((alt) => (
                        <button
                          key={alt.uuid}
                          onClick={() => onAddToBooking([alt.uuid])}
                          className="text-xs bg-white border border-green-300 text-green-700 rounded-full px-2.5 py-1 hover:bg-green-50 transition-colors"
                        >
                          {alt.name} {alt.subscription_included ? "(inkl.)" : alt.daily_rate > 0 ? `(${alt.daily_rate} kr/dag)` : "(gratis)"}
                          {" → Book"}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>

          {/* Shop suggestions for unavailable tools with no library alternatives */}
          {result.shop_suggestions.length > 0 && (
            <div className="bg-blue-50 rounded-lg border border-blue-200 p-3 space-y-2">
              <p className="text-sm font-semibold text-blue-800">🏪 Lokale butikker i nærheden</p>
              {result.shop_suggestions.map((s, i) => (
                <div key={i}>
                  {s.tools.length > 0 && (
                    <p className="text-xs font-medium text-blue-700 mb-0.5">{s.tools.join(", ")}:</p>
                  )}
                  <p className="text-xs text-blue-700">{s.suggestion}</p>
                </div>
              ))}
            </div>
          )}

          {/* Action bar */}
          <div className="flex gap-2 flex-wrap border-t border-gray-100 pt-3">
            {!result.all_available && result.results.some((r) => r.available) && (
              <button
                onClick={bookAvailable}
                className="px-4 py-2 text-sm font-medium text-white rounded-lg transition-opacity"
                style={{ backgroundColor: "#2d6a4f" }}
              >
                Book tilgængelige nu
              </button>
            )}
            {result.common_next_date && !result.all_available && (
              <button
                onClick={moveToCommon}
                className="px-4 py-2 text-sm font-medium bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors"
              >
                Flyt til {fmtDate(result.common_next_date)} (alle ledige)
              </button>
            )}
            {result.all_available && (
              <button
                onClick={() => onAddToBooking(result.results.map((r) => r.uuid))}
                className="px-4 py-2 text-sm font-medium text-white rounded-lg"
                style={{ backgroundColor: "#2d6a4f" }}
              >
                Book alle nu
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Donate tab (tools & materials)
// ─────────────────────────────────────────────────────────────────────────────

const CONDITION_LABELS: Record<string, string> = {
  good: "God stand",
  fair: "Rimelig stand",
  needs_repair: "Trænger til reparation",
};

const TOOL_CATEGORIES: Record<string, string> = {
  power_tools: "⚡ Elværktøj",
  hand_tools: "🔨 Håndværktøj",
  measuring: "📏 Måling",
  safety: "🦺 Sikkerhed",
  gardening: "🌱 Have",
  general: "🔧 Generelt",
};

const MATERIAL_CATEGORIES: Record<string, string> = {
  materials: "📦 Materialer",
  general: "🔧 Generelt",
};

function DonateTab({ itemType }: { itemType: "tool" | "material" }) {
  const isTool = itemType === "tool";
  const fileInputRef = useRef<HTMLInputElement>(null);

  // ── Donation settings (fetched from backend) ──────────────────────────────
  const [donationSettings, setDonationSettings] = useState<{
    tool_dropoff: { enabled: boolean; description: string; schedule: { day: string; from: string; to: string }[] };
    material_dropoff: { enabled: boolean; description: string; schedule: { day: string; from: string; to: string }[] };
    pickup_options: {
      community: { enabled: boolean; address: string; instructions: string; description: string; time_span: string };
      apoteket: { enabled: boolean; instructions: string; description: string; time_span: string };
    };
  } | null>(null);

  useEffect(() => {
    fetch("/api/toollib/donation-settings")
      .then((r) => r.json())
      .then(setDonationSettings)
      .catch(() => {/* use null — fallback gracefully */});
  }, []);

  const dropoff = donationSettings ? (isTool ? donationSettings.tool_dropoff : donationSettings.material_dropoff) : null;
  const pickupCommunity = donationSettings?.pickup_options?.community;
  const pickupApoteket = donationSettings?.pickup_options?.apoteket;
  const hasPickupOption = (pickupCommunity?.enabled || pickupApoteket?.enabled) ?? true;

  // ── Form state ────────────────────────────────────────────────────────────
  const [name, setName] = useState("");
  const [category, setCategory] = useState(isTool ? "general" : "materials");
  const [description, setDescription] = useState("");
  const [condition, setCondition] = useState("good");
  const [submitterName, setSubmitterName] = useState("");
  const [submitterEmail, setSubmitterEmail] = useState("");
  const [submitterPhone, setSubmitterPhone] = useState("");
  // pickupChoice: "delivery" | "community" | "apoteket"
  const [pickupChoice, setPickupChoice] = useState<"delivery" | "community" | "apoteket">("delivery");
  // Donor-provided pickup details
  const [donorAddress, setDonorAddress] = useState("");
  const [donorPickupDescription, setDonorPickupDescription] = useState("");
  const [donorTimeSpan, setDonorTimeSpan] = useState("");
  const [donorInstructions, setDonorInstructions] = useState("");
  // Image
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [aiAnalyzing, setAiAnalyzing] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState("");

  const categoryMap = isTool ? TOOL_CATEGORIES : MATERIAL_CATEGORIES;

  const handleImageFile = (file: File) => {
    setImageFile(file);
    const reader = new FileReader();
    reader.onload = (e) => setImagePreview(e.target?.result as string);
    reader.readAsDataURL(file);
  };

  const analyzeImage = async () => {
    if (!imageFile) return;
    setAiAnalyzing(true);
    try {
      const form = new FormData();
      form.append("image", imageFile);
      form.append("item_type", itemType);
      const res = await apiPostForm("ai/describe-donation", form) as { name_suggestion: string; description_suggestion: string };
      if (res.name_suggestion && !name) setName(res.name_suggestion);
      else if (res.name_suggestion) setName(res.name_suggestion);
      if (res.description_suggestion) setDescription(res.description_suggestion);
    } catch {
      /* silent — user can fill manually */
    } finally {
      setAiAnalyzing(false);
    }
  };

  const reset = () => {
    setName(""); setCategory(isTool ? "general" : "materials");
    setDescription(""); setCondition("good");
    setSubmitterName(""); setSubmitterEmail(""); setSubmitterPhone("");
    setPickupChoice("delivery");
    setDonorAddress(""); setDonorPickupDescription(""); setDonorTimeSpan(""); setDonorInstructions("");
    setImageFile(null); setImagePreview(null);
    setError(""); setSuccess(false);
  };

  const submit = async () => {
    if (!name.trim()) { setError("Angiv venligst et navn på " + (isTool ? "værktøjet" : "materialet")); return; }
    if (!submitterName.trim()) { setError("Angiv venligst dit navn"); return; }
    if (pickupChoice !== "delivery" && !donorAddress.trim()) {
      setError("Angiv venligst en afhentningsadresse"); return;
    }
    setSubmitting(true); setError("");
    try {
      await apiPost("submissions", {
        item_name: name.trim(),
        item_type: itemType,
        category,
        description: description.trim(),
        condition,
        image_url: imagePreview ?? "",
        submitter_name: submitterName.trim(),
        submitter_email: submitterEmail.trim(),
        submitter_phone: submitterPhone.trim(),
        pickup_preference: pickupChoice === "delivery" ? "delivery" : "pickup",
        storage_preference: pickupChoice !== "delivery" ? donorAddress.trim() : "",
        pickup_address: donorAddress.trim(),
        pickup_description: donorPickupDescription.trim(),
        pickup_time_span: donorTimeSpan.trim(),
        pickup_instructions: donorInstructions.trim(),
      });
      setSuccess(true);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : "Fejl ved indsendelse");
    } finally {
      setSubmitting(false);
    }
  };

  if (success) {
    return (
      <div className="max-w-lg mx-auto">
        <div className="bg-green-50 border border-green-200 rounded-2xl p-8 text-center space-y-4">
          <div className="text-5xl">🎉</div>
          <h3 className="text-lg font-bold text-green-900">
            Tak for din donation!
          </h3>
          <p className="text-sm text-green-700">
            {isTool ? "Værktøjet" : "Materialet"} er modtaget og vil blive gennemgået af vores team.
            {pickupChoice !== "delivery"
              ? " Vi kontakter dig for at aftale afhentning."
              : dropoff?.description
              ? ` ${dropoff.description}.`
              : " Bring det venligst til Materialebiblioteket."}
          </p>
          <button
            onClick={reset}
            className="px-6 py-2.5 text-sm text-white rounded-xl font-medium"
            style={{ backgroundColor: "#2d6a4f" }}
          >
            Donér endnu {isTool ? "et værktøj" : "et materiale"}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-lg mx-auto space-y-5">
      {/* Header */}
      <div className={`border rounded-xl p-4 ${isTool ? "bg-amber-50 border-amber-200" : "bg-teal-50 border-teal-200"}`}>
        <h3 className={`font-semibold mb-1 ${isTool ? "text-amber-900" : "text-teal-900"}`}>
          {isTool ? "🎁 Donér Værktøj" : "♻️ Donér Materialer"}
        </h3>
        <p className={`text-sm ${isTool ? "text-amber-700" : "text-teal-700"}`}>
          {isTool
            ? "Har du et værktøj, du ikke bruger mere? Donér det til Materialebiblioteket og hjælp andre!"
            : "Har du overskudsmaterialer? Donér dem til Materialebiblioteket og giv dem nyt liv!"}
        </p>
      </div>

      {/* Dropoff times info */}
      {dropoff && dropoff.enabled && (dropoff.description || dropoff.schedule.length > 0) && (
        <div className="bg-green-50 border border-green-200 rounded-xl p-4 space-y-2">
          <h4 className="text-sm font-semibold text-green-800">🕐 Afleveringstider</h4>
          {dropoff.description && <p className="text-sm text-green-700">{dropoff.description}</p>}
          {dropoff.schedule.length > 0 && (
            <div className="grid grid-cols-2 gap-1.5 mt-1">
              {dropoff.schedule.map((slot, i) => (
                <div key={i} className="flex items-center gap-2 text-xs text-green-800 bg-white border border-green-100 rounded-lg px-2.5 py-1.5">
                  <span className="font-medium w-20">{slot.day}</span>
                  <span>{slot.from} – {slot.to}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Item info */}
      <div className="bg-white rounded-xl border border-gray-200 p-4 space-y-3">
        <h4 className="text-sm font-semibold text-gray-700">
          Om {isTool ? "værktøjet" : "materialet"}
        </h4>

        {/* Image upload */}
        <div>
          <label className="text-xs text-gray-600 font-medium block mb-1">Billede (valgfrit)</label>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            className="hidden"
            onChange={(e) => { const f = e.target.files?.[0]; if (f) handleImageFile(f); }}
          />
          {imagePreview ? (
            <div className="space-y-2">
              <div className="relative inline-block">
                {/* eslint-disable-next-line @next/next/no-img-element */}
                <img src={imagePreview} alt="preview" className="w-full max-h-48 object-contain rounded-lg border border-gray-200 bg-gray-50" />
                <button
                  onClick={() => { setImageFile(null); setImagePreview(null); if (fileInputRef.current) fileInputRef.current.value = ""; }}
                  className="absolute top-1 right-1 bg-white border border-gray-200 rounded-full w-6 h-6 text-xs text-gray-500 hover:text-red-500 flex items-center justify-center shadow"
                >✕</button>
              </div>
              <button
                onClick={analyzeImage}
                disabled={aiAnalyzing}
                className="flex items-center gap-2 px-3 py-1.5 text-sm text-white rounded-lg disabled:opacity-50"
                style={{ backgroundColor: "#5b21b6" }}
              >
                {aiAnalyzing
                  ? <><span className="animate-spin inline-block">⚙️</span> AI analyserer&#8230;</>                  : <>✨ AI: Analyser billede og udfyld navn/beskrivelse</>}
              </button>
            </div>
          ) : (
            <button
              onClick={() => fileInputRef.current?.click()}
              className="w-full border-2 border-dashed border-gray-200 rounded-lg py-6 text-sm text-gray-400 hover:border-green-400 hover:text-green-600 transition-colors"
            >
              📷 Vælg billede
            </button>
          )}
        </div>

        <div>
          <label className="text-xs text-gray-600 font-medium block mb-1">
            Navn *
          </label>
          <input
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder={isTool ? "Eks: Boremaskine, Skruetræk ker..." : "Eks: Træbrædder, Maling, Mursten..."}
            className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:border-green-400"
          />
        </div>

        <div className="flex gap-3">
          <div className="flex-1">
            <label className="text-xs text-gray-600 font-medium block mb-1">Kategori</label>
            <select
              value={category}
              onChange={(e) => setCategory(e.target.value)}
              className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none"
            >
              {Object.entries(categoryMap).map(([k, v]) => (
                <option key={k} value={k}>{v}</option>
              ))}
            </select>
          </div>
          <div className="flex-1">
            <label className="text-xs text-gray-600 font-medium block mb-1">Stand</label>
            <select
              value={condition}
              onChange={(e) => setCondition(e.target.value)}
              className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none"
            >
              {Object.entries(CONDITION_LABELS).map(([k, v]) => (
                <option key={k} value={k}>{v}</option>
              ))}
            </select>
          </div>
        </div>

        <div>
          <label className="text-xs text-gray-600 font-medium block mb-1">Beskrivelse (valgfrit)</label>
          <textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder={isTool ? "Mærke, model, tilbehør inkluderet..." : "Mængde, dimensioner, tilstand..."}
            rows={3}
            className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none resize-none"
          />
        </div>
      </div>

      {/* Pickup / delivery choice */}
      <div className="bg-white rounded-xl border border-gray-200 p-4 space-y-3">
        <h4 className="text-sm font-semibold text-gray-700">Levering</h4>
        <div className={`grid gap-3 ${hasPickupOption ? "grid-cols-2" : "grid-cols-1"}`}>
          {/* Jeg leverer */}
          <button
            type="button"
            onClick={() => setPickupChoice("delivery")}
            className={`rounded-xl border-2 p-4 text-left transition-all ${
              pickupChoice === "delivery" ? "border-green-500 bg-green-50" : "border-gray-200 bg-white hover:bg-gray-50"
            }`}
          >
            <div className="text-2xl mb-1">🚶</div>
            <div className="text-sm font-semibold text-gray-800">Jeg leverer</div>
            <div className="text-xs text-gray-500 mt-0.5">
              Jeg bringer det selv til Materialebiblioteket
            </div>
          </button>

          {/* Community pickup */}
          {pickupCommunity?.enabled && (
            <button
              type="button"
              onClick={() => setPickupChoice("community")}
              className={`rounded-xl border-2 p-4 text-left transition-all ${
                pickupChoice === "community" ? "border-blue-500 bg-blue-50" : "border-gray-200 bg-white hover:bg-gray-50"
              }`}
            >
              <div className="text-2xl mb-1">🏘️</div>
              <div className="text-sm font-semibold text-gray-800">Lokal afhentning</div>
              <div className="text-xs text-gray-500 mt-0.5">
                {pickupCommunity.description || "Afhentning i lokalområdet"}
              </div>
            </button>
          )}

          {/* Apoteket pickup */}
          {pickupApoteket?.enabled && (
            <button
              type="button"
              onClick={() => setPickupChoice("apoteket")}
              className={`rounded-xl border-2 p-4 text-left transition-all ${
                pickupChoice === "apoteket" ? "border-purple-500 bg-purple-50" : "border-gray-200 bg-white hover:bg-gray-50"
              }`}
            >
              <div className="text-2xl mb-1">🚐</div>
              <div className="text-sm font-semibold text-gray-800">I henter</div>
              <div className="text-xs text-gray-500 mt-0.5">
                {pickupApoteket.description || "Afhentning fra Lokalt Innovations hus i Haslev by"}
              </div>
            </button>
          )}

          {/* Fallback when no settings loaded yet */}
          {!donationSettings && (
            <button
              type="button"
              onClick={() => setPickupChoice("apoteket")}
              className={`rounded-xl border-2 p-4 text-left transition-all ${
                pickupChoice === "apoteket" ? "border-blue-500 bg-blue-50" : "border-gray-200 bg-white hover:bg-gray-50"
              }`}
            >
              <div className="text-2xl mb-1">🚐</div>
              <div className="text-sm font-semibold text-gray-800">I henter</div>
              <div className="text-xs text-gray-500 mt-0.5">Kan I afhente det hos mig?</div>
            </button>
          )}
        </div>

        {/* Delivery info */}
        {pickupChoice === "delivery" && (
          <div className="text-xs text-gray-500 bg-gray-50 rounded-lg px-3 py-2 space-y-1">
            <p>📍 Bring {isTool ? "værktøjet" : "materialet"} til{" "}
              <span className="font-medium">Materialebiblioteket</span>{dropoff?.description ? ":" : " i åbningstiden."}
            </p>
            {dropoff?.description && <p>{dropoff.description}</p>}
          </div>
        )}

        {/* Donor pickup input fields — community or apoteket */}
        {(pickupChoice === "community" || pickupChoice === "apoteket") && (
          <div className={`space-y-3 rounded-xl border p-3 ${
            pickupChoice === "community" ? "border-blue-200 bg-blue-50" : "border-purple-200 bg-purple-50"
          }`}>
            <p className="text-xs font-medium text-gray-600">
              {pickupChoice === "community"
                ? "🏘️ Udfyld afhentningsoplysninger — din adresse vises offentligt når donationen er godkendt."
                : "🚐 Udfyld afhentningsoplysninger så vi kan aftale afhentning."}
            </p>
            <div>
              <label className="text-xs text-gray-600 font-medium block mb-1">
                Afhentningsadresse *{pickupChoice === "community" ? " (vises offentligt)" : ""}
              </label>
              <input
                value={donorAddress}
                onChange={(e) => setDonorAddress(e.target.value)}
                placeholder="Vejnavn og husnummer, postnummer og by"
                className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:border-blue-400 bg-white"
              />
            </div>
            <div>
              <label className="text-xs text-gray-600 font-medium block mb-1">Beskrivelse (valgfrit)</label>
              <input
                value={donorPickupDescription}
                onChange={(e) => setDonorPickupDescription(e.target.value)}
                placeholder={pickupChoice === "community" ? "Eks: Portæfælde ved indgangen, ring på navn..." : "Eks: Kassen står klar i forhallen..."}
                className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none bg-white"
              />
            </div>
            <div>
              <label className="text-xs text-gray-600 font-medium block mb-1">Tidsrum (valgfrit)</label>
              <input
                value={donorTimeSpan}
                onChange={(e) => setDonorTimeSpan(e.target.value)}
                placeholder="Eks: Hverdage 16:00–19:00, weekends 10:00–14:00"
                className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none bg-white"
              />
            </div>
            <div>
              <label className="text-xs text-gray-600 font-medium block mb-1">Afhentningsinstruktioner (valgfrit)</label>
              <textarea
                value={donorInstructions}
                onChange={(e) => setDonorInstructions(e.target.value)}
                placeholder="Eks: Ring på dørklokken, parkering langs vejen, spørg efter Mette..."
                rows={2}
                className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none resize-none bg-white"
              />
            </div>
            {pickupChoice === "community" && pickupCommunity?.instructions && (
              <p className="text-xs text-blue-600 italic">ℹ️ Generel info: {pickupCommunity.instructions}</p>
            )}
            {pickupChoice === "apoteket" && pickupApoteket?.instructions && (
              <p className="text-xs text-purple-600 italic">ℹ️ {pickupApoteket.instructions}</p>
            )}
          </div>
        )}
      </div>

      {/* Submitter info */}
      <div className="bg-white rounded-xl border border-gray-200 p-4 space-y-3">
        <h4 className="text-sm font-semibold text-gray-700">Dine kontaktoplysninger</h4>

        <div>
          <label className="text-xs text-gray-600 font-medium block mb-1">Navn *</label>
          <input
            value={submitterName}
            onChange={(e) => setSubmitterName(e.target.value)}
            placeholder="Dit fulde navn"
            className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:border-green-400"
          />
        </div>

        <div className="flex gap-3">
          <div className="flex-1">
            <label className="text-xs text-gray-600 font-medium block mb-1">E-mail (valgfrit)</label>
            <input
              type="email"
              value={submitterEmail}
              onChange={(e) => setSubmitterEmail(e.target.value)}
              placeholder="din@email.dk"
              className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none"
            />
          </div>
          <div className="flex-1">
            <label className="text-xs text-gray-600 font-medium block mb-1">Telefon (valgfrit)</label>
            <input
              type="tel"
              value={submitterPhone}
              onChange={(e) => setSubmitterPhone(e.target.value)}
              placeholder="12 34 56 78"
              className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none"
            />
          </div>
        </div>
      </div>

      {error && (
        <p className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
          {error}
        </p>
      )}

      <button
        onClick={submit}
        disabled={submitting}
        className="w-full py-3 rounded-xl text-white font-medium text-sm disabled:opacity-50"
        style={{ backgroundColor: "#2d6a4f" }}
      >
        {submitting ? "⏳ Sender..." : `🎁 Send donation`}
      </button>
    </div>
  );
}

